import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Container, 
  Paper, 
  Typography, 
  CircularProgress, 
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc, getFirestore } from 'firebase/firestore';

const PaymentSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshSubscription } = useSubscription();
  const { currentUser } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(true);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [userDocData, setUserDocData] = useState<any>(null);
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);

  // Extract session ID from URL if present
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get('session_id');

  // Function to fetch user document from Firestore
  const fetchUserDocument = async () => {
    if (!currentUser) return;
    
    try {
      setIsLoadingUserData(true);
      const db = getFirestore();
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        setUserDocData(userDoc.data());
      } else {
        console.log('No user document found');
      }
    } catch (error) {
      console.error('Error fetching user document:', error);
    } finally {
      setIsLoadingUserData(false);
    }
  };

  useEffect(() => {
    // Immediately try to refresh subscription status
    const refreshData = async () => {
      try {
        setIsRefreshing(true);
        setRefreshError(null);
        console.log('Payment success page: Refreshing subscription status...');
        await refreshSubscription();
        console.log('Payment success page: Subscription refreshed successfully');
        
        // After refreshing subscription, fetch user document to verify
        await fetchUserDocument();
      } catch (error) {
        console.error('Error refreshing subscription on payment success page:', error);
        setRefreshError('Unable to refresh subscription status. Your account will update shortly.');
      } finally {
        setIsRefreshing(false);
      }
    };

    refreshData();
    
    // Auto-redirect to home after 10 seconds (longer to give more time to check debug info)
    const redirectTimer = setTimeout(() => {
      navigate('/');
    }, 10000);

    return () => clearTimeout(redirectTimer);
  }, [refreshSubscription, navigate]);

  // Check if the user has premium status from the fetched data
  const hasPremiumStatus = userDocData?.subscriptionTier === 'premium';

  return (
    <Container maxWidth="sm">
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
        <Typography variant="h4" component="h1" gutterBottom>
          Payment Successful!
        </Typography>
        
        {sessionId && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Session ID: {sessionId}
          </Typography>
        )}
        
        <Typography variant="body1" paragraph>
          Thank you for your purchase. Your account has been upgraded to Premium.
        </Typography>
        
        {isRefreshing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Updating your subscription status...
            </Typography>
          </Box>
        ) : refreshError ? (
          <Typography variant="body2" color="error" paragraph>
            {refreshError}
          </Typography>
        ) : hasPremiumStatus ? (
          <Typography variant="body2" color="success.main" paragraph>
            Your subscription status has been verified as Premium! You now have unlimited access.
          </Typography>
        ) : (
          <Typography variant="body2" color="warning.main" paragraph>
            Your payment was processed, but your premium status is not showing yet. This may take a moment to update.
            Use the refresh button in the app if needed.
          </Typography>
        )}
        
        <Divider sx={{ width: '100%', my: 2 }} />
        
        {/* Debug information (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <Accordion sx={{ width: '100%', mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Debug Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" align="left">
                Session ID: {sessionId || 'Not provided'}
              </Typography>
              
              <Typography variant="body2" align="left" sx={{ mt: 1 }}>
                User ID: {currentUser?.uid || 'Not signed in'}
              </Typography>
              
              <Typography variant="body2" align="left" sx={{ mt: 1 }}>
                Email: {currentUser?.email || 'Not available'}
              </Typography>
              
              <Typography variant="subtitle2" align="left" sx={{ mt: 2 }}>
                User Document Data:
              </Typography>
              
              {isLoadingUserData ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="body2">Loading user data...</Typography>
                </Box>
              ) : userDocData ? (
                <Box component="pre" sx={{ 
                  mt: 1, 
                  p: 1, 
                  bgcolor: 'background.paper', 
                  border: '1px solid', 
                  borderColor: 'divider',
                  overflowX: 'auto',
                  textAlign: 'left',
                  fontSize: '0.75rem'
                }}>
                  {JSON.stringify(userDocData, null, 2)}
                </Box>
              ) : (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  No user document data found
                </Typography>
              )}
              
              <Button 
                variant="outlined" 
                size="small" 
                onClick={fetchUserDocument} 
                sx={{ mt: 2 }}
                disabled={isLoadingUserData}
              >
                Refresh User Data
              </Button>
            </AccordionDetails>
          </Accordion>
        )}
        
        <Typography variant="body2" color="text.secondary" paragraph>
          You will be redirected to the home page automatically in a few seconds.
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Return to Home
        </Button>
      </Paper>
    </Container>
  );
};

export default PaymentSuccessPage;